import { useCallback, useState } from "react";
import { useForm } from "@tanstack/react-form";
import { Schema } from "effect";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { reportOperationalError } from "../../../../../lib/operational-error-telemetry";
import { normalizeDateOfBirthForApi } from "../../../components/DateOfBirthInput";

/**
 * Form schema of the shared kiosk patient lookup (scheduler and prescription,
 * KO-07/FC-07). The first name is optional and only requested after the bridge
 * reports that last name and date of birth are not unique.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * Schema.decodeUnknownSync(PatientLookupSchema)({ firstName: "", lastName: "Muster", dateOfBirth: "1980-01-01" });
 * ```
 */
export const PatientLookupSchema = Schema.Struct({
  firstName: Schema.String,
  lastName: Schema.String.pipe(
    Schema.check(Schema.isMinLength(1, { message: "validation.required" })),
  ),
  dateOfBirth: Schema.String.pipe(
    Schema.check(Schema.isMinLength(1, { message: "validation.required" })),
  ),
});

/**
 * Values of the shared kiosk patient lookup form.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const values: PatientLookupValues = { firstName: "", lastName: "Muster", dateOfBirth: "1980-01-01" };
 * ```
 */
export type PatientLookupValues = Schema.Schema.Type<typeof PatientLookupSchema>;

/**
 * Supported insurance of a patient found by the lookup.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const insurance: PatientLookupInsurance = "GKV";
 * ```
 */
export type PatientLookupInsurance = "GKV" | "PKV" | "SELFPAY";

/**
 * Result state of the shared kiosk patient lookup. Errors carry no message:
 * the patient only ever sees localized copy (KO-01).
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const state: PatientLookupState = { status: "idle" };
 * ```
 */
export type PatientLookupState =
  | { readonly status: "idle" }
  | { readonly status: "searching" }
  | {
      readonly status: "found";
      readonly patientId: string;
      readonly insurance: PatientLookupInsurance;
    }
  | { readonly status: "ambiguous"; readonly hint: "firstname" | "additional" }
  | { readonly status: "not_found" }
  | { readonly status: "error" };

const EMPTY_LOOKUP: PatientLookupValues = {
  firstName: "",
  lastName: "",
  dateOfBirth: "",
};

/**
 * Shared patient lookup: one form (Vorname, Nachname, Geburtsdatum), one
 * bridge search and one result state for the scheduler and the prescription
 * flow. The first-name field is requested (and stays visible) once the bridge
 * answers that the search is ambiguous by first name. Editing any field after
 * a result returns the lookup to `idle`, so a confirm action can never refer
 * to values the patient has since changed.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * const lookup = usePatientLookup({ component: "PrescriptionPatientLookup" });
 * <KioskPatientLookupPanel lookup={lookup} … />
 * ```
 */
export function usePatientLookup({ component }: { readonly component: string }) {
  const [state, setState] = useState<PatientLookupState>({ status: "idle" });
  const [firstNameRequested, setFirstNameRequested] = useState(false);

  const form = useForm({
    defaultValues: EMPTY_LOOKUP,
    validators: {
      onChange: Schema.toStandardSchemaV1(PatientLookupSchema),
    },
    onSubmit: async ({ value }) => {
      setState({ status: "searching" });
      try {
        const result = await docCirrusRpcClient.frontdesk.searchPatient({
          lastname: value.lastName.trim(),
          dob: normalizeDateOfBirthForApi(value.dateOfBirth),
          firstname: value.firstName.trim() || undefined,
        });
        if (result.status === "FOUND") {
          if (result.insurance === "UNKNOWN") {
            void reportOperationalError({
              action: "kiosk.patient-lookup-insurance-unrecognized",
              severity: "error",
              source: "invariant",
              component,
              error: new Error("Patient search returned no supported insurance type"),
              context: { insuranceRecognized: false },
            });
            setState({ status: "error" });
            return;
          }
          setState({
            status: "found",
            patientId: result.patientId,
            insurance: result.insurance,
          });
          return;
        }
        if (result.status === "AMBIGUOUS") {
          if (result.hint === "firstname") setFirstNameRequested(true);
          setState({ status: "ambiguous", hint: result.hint });
          return;
        }
        setState({ status: "not_found" });
      } catch (error) {
        frontdeskDiagnosticLogger.error("Patient search failed", {
          error,
          component,
          hasLastname: Boolean(value.lastName),
          hasDob: Boolean(value.dateOfBirth),
          hasFirstname: Boolean(value.firstName),
        });
        setState({ status: "error" });
      }
    },
  });

  const markEdited = useCallback(function returnLookupToIdleAfterEdit() {
    setState((current) =>
      current.status === "idle" || current.status === "searching"
        ? current
        : { status: "idle" },
    );
  }, []);

  return { form, state, firstNameRequested, markEdited };
}

/**
 * Value returned by {@link usePatientLookup}.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * type Lookup = PatientLookup;
 * ```
 */
export type PatientLookup = ReturnType<typeof usePatientLookup>;
